import { isDefined } from "@clipboard-health/util-ts";
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonFab,
  IonHeader,
  IonLoading,
  IonPage,
  IonText,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { TabRouterPath } from "@src/app/routing/constant/tabRoute";
import { setLicensesCount } from "@src/app/store/session";
import { logEvent } from "@src/appV2/lib/analytics";
import { LicenseStatuses } from "@src/appV2/Licenses/api/types";
import { useGetLicenses } from "@src/appV2/Licenses/api/useGetLicenses";
import { USER_EVENTS } from "@src/constants/userEvents";
import { useDefinedUserId } from "@store/helperHooks";
import { FC, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { ILicenseDetails } from "./interfaces";
import { License } from "./license";
import { getLicensesCountByStatus, getLicensesSummaryText } from "./utils";

const LicenseManager: FC<{}> = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const userId = useDefinedUserId();

  const onAddLicenseClick = () => {
    logEvent(USER_EVENTS.ADD_LICENSE_TAPPED);
    history.push(TabRouterPath.ADD_LICENSE);
  };

  const { isLoading, data: licenses } = useGetLicenses(
    {
      workerId: userId,
    },
    {
      userErrorMessage: "Failed to get licenses",
    }
  );

  useEffect(() => {
    if (!isDefined(licenses)) {
      return;
    }

    const licensesCountByStatus = getLicensesCountByStatus(licenses as ILicenseDetails[]);
    dispatch(setLicensesCount(userId));
    logEvent(USER_EVENTS.LICENSES_VIEWED, {
      activeCount:
        (licensesCountByStatus[LicenseStatuses.ACTIVE] || 0) +
        (licensesCountByStatus[LicenseStatuses.EXPIRING] || 0),
    });
  }, [licenses, dispatch, userId]);

  const licensesSummaryText = useMemo(() => {
    if (!licenses) {
      return [];
    }

    const licensesCountByStatus = getLicensesCountByStatus(licenses as ILicenseDetails[]);
    return getLicensesSummaryText(licensesCountByStatus);
  }, [licenses]);

  return (
    <IonPage>
      <IonHeader no-border>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton text="" defaultHref="/home/account" mode="ios" />
          </IonButtons>
          <IonTitle>Licenses</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonLoading isOpen={isLoading} backdropDismiss={true} />
      <IonContent className="license-manager">
        {licenses?.length ? (
          <>
            <IonText className="licenses-summary-text">
              <p style={{ marginTop: "20px", marginBottom: "0px" }}>
                {licensesSummaryText.map((summaryText, index) => {
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <span key={index}>
                      {!!index && ", "}
                      <span style={{ whiteSpace: "nowrap" }}>{summaryText}</span>
                    </span>
                  );
                })}
                {}
              </p>
            </IonText>
            {licenses.map((license) => {
              return <License license={license as ILicenseDetails} key={license.id} />;
            })}
            <div style={{ height: "60px" }}></div>
            {/* Empty div to scroll license card above fab button*/}
          </>
        ) : (
          <IonText className="add-license-text">
            <p style={{ marginTop: "20px" }}>
              {isLoading ? "Loading Licenses..." : "Add a License to start working!"}
            </p>
          </IonText>
        )}
        <IonFab vertical="bottom" slot="fixed" className="add-license-fab">
          <IonButton
            data-testid="add-license-button"
            expand="block"
            className="add-license-button"
            fill="outline"
            onClick={onAddLicenseClick}
          >
            Add License
          </IonButton>
        </IonFab>
      </IonContent>
    </IonPage>
  );
};

export { LicenseManager };
